import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import empty_box from "../../images/empty_box.png";
import Client from "../../utils/client";

const InKindContribution = ({ contributionType = "donations", category = "crops" }) => {
    const [viewMode, setViewMode] = useState("both"); // "contributions", "orders", or "both"
    const navigate = useNavigate();

    // Get user info from localStorage to determine permissions
    const userType = localStorage.getItem("type");
    const userCoopType = localStorage.getItem("cooperativeType");
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("token");
    const isConsumerCooperative = userType === "cooperative" && userCoopType === "consumer";
    const isServiceProvider = userType === "service_provider";

    useEffect(() => {
        console.log("Current path:", window.location.pathname);
        console.log("Props:", { contributionType, category });

        // Auto-redirect service providers from generic donations to services donations
        if (window.location.pathname === "/home/profile/donations" && isServiceProvider) {
            console.log("Redirecting service provider to services donations");
            navigate("/home/profile/services/my-donations", { replace: true });
            return;
        }

        // Auto-redirect consumer cooperatives from generic donations to crops needs
        if (window.location.pathname === "/home/profile/donations" && isConsumerCooperative) {
            console.log("Redirecting consumer cooperative to crops needs");
            navigate("/home/profile/crops/my-needs", { replace: true });
            return;
        }
    }, [navigate, isServiceProvider, isConsumerCooperative, contributionType, category]);

    // Determine API endpoint based on contribution type and category
    const getEndpoint = () => {
        const basePath = category === "services" ? "/services" : "/offers";

        if (contributionType === "donations") {
            return `${basePath}/my-donations`;
        } else if (contributionType === "needs") {
            return `${basePath}/my-needs`;
        }
        return `${basePath}/my-donations`;
    };

    // Check if user has access to this category/type combination
    const hasAccess = () => {
        // For crops donations: All users except service providers can donate crops
        if (category === "crops" && contributionType === "donations") {
            return isServiceProvider;
        }

        // For crops needs: Only consumer cooperatives can request crops
        if (category === "crops" && contributionType === "needs") {
            return isConsumerCooperative;
        }

        // For services donations: Only service providers can donate services
        if (category === "services" && contributionType === "donations") {
            return isServiceProvider;
        }

        // For services needs: Only consumer cooperatives can request services
        if (category === "services" && contributionType === "needs") {
            return isConsumerCooperative;
        }

        return false;
    };

    // Fetch contributions
    const {
        data: contributions,
        isLoading: isFetchingContributions,
        isError: isContributionsError,
        error: contributionsError,
        refetch: refetchContributions
    } = useQuery({
        queryKey: ["inKindContributions", contributionType, category, userId],
        queryFn: async () => {
            if (!hasAccess()) {
                throw new Error("غير مصرح لك بالوصول لهذه الصفحة");
            }

            const endpoint = getEndpoint();

            try {
                const response = await Client.get(endpoint);

                if (Array.isArray(response.data)) {
                    return response.data;
                } else if (response.data && typeof response.data === 'object') {
                    if (Array.isArray(response.data.data)) {
                        return response.data.data;
                    } else if (response.data.items) {
                        return response.data.items;
                    }
                }

                return [];
            } catch (error) {
                console.error("Error fetching contributions:", error);
                throw error;
            }
        },
        enabled: hasAccess() && (viewMode === "contributions" || viewMode === "both"),
        retry: 1,
    });

    // Fetch in-kind orders
    const {
        data: orders,
        isLoading: isFetchingOrders,
        isError: isOrdersError,
        error: ordersError,
        refetch: refetchOrders
    } = useQuery({
        queryKey: ["inKindOrders", category, userId, contributionType],
        queryFn: async () => {
            if (!hasAccess()) {
                throw new Error("غير مصرح لك بالوصول لهذه الصفحة");
            }

            const userRole = contributionType === "donations" ? "seller" : "buyer";

            try {
                const response = await Client.get(`/orders?type=${userRole}&isInKind=true`);

                let ordersData = [];

                if (Array.isArray(response.data)) {
                    ordersData = response.data;
                } else if (response.data && typeof response.data === 'object') {
                    if (Array.isArray(response.data.data)) {
                        ordersData = response.data.data;
                    } else if (response.data.items) {
                        ordersData = response.data.items;
                    } else if (response.data.orders) {
                        ordersData = response.data.orders;
                    }
                }

                // Filter orders based on category
                const filteredOrders = ordersData.filter(order => {
                    if (!order || !order.negotiation) return false;

                    const isCropOrder = order.negotiation.type === "Crop" || order.negotiation.type === "CROP";
                    const isServiceOrder = order.negotiation.type === "Service" || order.negotiation.type === "SERVICE";

                    if (category === "crops") {
                        return isCropOrder;
                    } else if (category === "services") {
                        return isServiceOrder;
                    }
                    return false;
                });

                return filteredOrders;
            } catch (error) {
                console.error("Error fetching orders:", error);
                throw error;
            }
        },
        enabled: hasAccess() && (viewMode === "orders" || viewMode === "both"),
        retry: 1,
    });

    // Function to get order status color
    const getOrderStatusColor = (status) => {
        switch (status) {
            case "Dispatched": return "bg-blue-100 text-blue-700 border border-blue-200";
            case "InTransit": return "bg-yellow-100 text-yellow-700 border border-yellow-200";
            case "Delivered": return "bg-orange-100 text-orange-700 border border-orange-200";
            case "Received": return "bg-green-100 text-green-700 border border-green-200";
            case "Cancelled": return "bg-red-100 text-red-700 border border-red-200";
            default: return "bg-gray-100 text-gray-700 border border-gray-200";
        }
    };

    // Function to translate order status
    const translateOrderStatus = (status) => {
        if (!status) return "جاري المعالجة";

        switch (status) {
            case "Dispatched": return "تم التحميل";
            case "InTransit": return "في الطريق";
            case "Delivered": return "تم التسليم";
            case "Received": return "تم الاستلام";
            case "Cancelled": return "ملغي";
            default: return status;
        }
    };

    // Function to get contribution type color
    const getContributionTypeColor = () => {
        if (category === "services") {
            return "bg-orange-50 border-orange-200";
        }
        return contributionType === "donations" ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200";
    };

    // Function to get contribution type icon
    const getContributionTypeIcon = () => {
        if (category === "services") return "🚜";
        return contributionType === "donations" ? "🎁" : "🙏";
    };

    // Format contribution data
    const formatContributionData = (contribution) => {
        if (!contribution) return {};

        return {
            id: contribution.id || contribution._id || Math.random(),
            offer: contribution.offer || contribution.title || contribution.name || "غير محدد",
            description: contribution.description || "",
            status: contribution.status || "unknown",
            quantity: contribution.quantity || contribution.donation_capacity || contribution.amount || "غير محدد",
            category: contribution.category || {},
            subCategory: contribution.subCategory || {},
            service: contribution.service || {},
            vehicleType: contribution.vehicleType || {},
            _unit: contribution._unit || contribution.unit || {},
            _state: contribution._state || contribution.state || {},
            _city: contribution._city || contribution.city || {},
            created_at: contribution.created_at || contribution.createdAt || new Date().toISOString(),
            offer_type: contribution.offer_type || "donate"
        };
    };

    // Format order data
    const formatOrderData = (order) => {
        if (!order) return {};

        return {
            id: order.id || order._id || Math.random(),
            inKindStatus: order.inKindStatus || order.status || "Pending",
            quantity: order.quantity || 0,
            price: order.price || order.amount || 0,
            deliveryDate: order.deliveryDate || order.delivery_date || null,
            createdAt: order.createdAt || order.created_at || new Date().toISOString(),
            dispatchNotes: order.dispatchNotes || "",
            deliveryNotes: order.deliveryNotes || "",
            receiptNotes: order.receiptNotes || "",
            invoice: order.invoice || {},
            negotiation: order.negotiation || {
                type: category === "crops" ? "Crop" : "Service",
                itemId: null
            }
        };
    };

    // Get available tabs based on user type
    const getAvailableTabs = () => {
        const tabs = [];

        if (!isConsumerCooperative) {
            tabs.push({
                id: "crops-donations",
                label: "تبرعات المحاصيل",
                path: "/home/profile/crops/my-donations",
                active: category === "crops" && contributionType === "donations",
                color: "text-green-600 border-green-500"
            });
        }

        if (isConsumerCooperative) {
            tabs.push({
                id: "crops-needs",
                label: "طلبات المحاصيل",
                path: "/home/profile/crops/my-needs",
                active: category === "crops" && contributionType === "needs",
                color: "text-blue-600 border-blue-500"
            });
        }

        if (isServiceProvider) {
            tabs.push({
                id: "services-donations",
                label: "تبرعات الخدمات",
                path: "/home/profile/services/my-donations",
                active: category === "services" && contributionType === "donations",
                color: "text-orange-600 border-orange-500"
            });
        }

        if (isConsumerCooperative) {
            tabs.push({
                id: "services-needs",
                label: "طلبات الخدمات",
                path: "/home/profile/services/my-needs",
                active: category === "services" && contributionType === "needs",
                color: "text-purple-600 border-purple-500"
            });
        }

        return tabs;
    };

    // Calculate statistics
    const calculateStats = () => {
        const formattedContributions = Array.isArray(contributions)
            ? contributions.map(formatContributionData)
            : [];

        const formattedOrders = Array.isArray(orders)
            ? orders.map(formatOrderData)
            : [];

        return {
            contributions: formattedContributions.length,
            orders: formattedOrders.length,
            active: formattedContributions.filter(c => c.status === 'active' || c.status === 'Active').length,
            completed: formattedOrders.filter(o => o.inKindStatus === 'Received' || o.inKindStatus === 'RECEIVED').length,
            inProgress: formattedOrders.filter(o =>
                o.inKindStatus === 'Dispatched' ||
                o.inKindStatus === 'InTransit' ||
                o.inKindStatus === 'Delivered'
            ).length
        };
    };

    const stats = calculateStats();

    // Render statistics card
    const renderStatsCard = () => {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="text-sm text-gray-500">المساهمات</div>
                    <div className="text-2xl font-bold text-gray-800 mt-1">{stats.contributions}</div>
                    <div className="text-xs text-gray-400 mt-1">إجمالي المساهمات</div>
                </div>

                <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="text-sm text-gray-500">الطلبات</div>
                    <div className="text-2xl font-bold text-blue-600 mt-1">{stats.orders}</div>
                    <div className="text-xs text-gray-400 mt-1">إجمالي الطلبات</div>
                </div>

                <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="text-sm text-gray-500">نشطة</div>
                    <div className="text-2xl font-bold text-green-600 mt-1">{stats.active}</div>
                    <div className="text-xs text-gray-400 mt-1">مساهمات نشطة</div>
                </div>

                <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="text-sm text-gray-500">قيد التنفيذ</div>
                    <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.inProgress}</div>
                    <div className="text-xs text-gray-400 mt-1">طلبات جارية</div>
                </div>

                <div className="bg-white p-4 rounded-xl shadow-sm border">
                    <div className="text-sm text-gray-500">مكتملة</div>
                    <div className="text-2xl font-bold text-purple-600 mt-1">{stats.completed}</div>
                    <div className="text-xs text-gray-400 mt-1">طلبات مكتملة</div>
                </div>
            </div>
        );
    };

    // Render a single contribution item
    const renderContributionItem = (contribution, index) => {
        const formatted = formatContributionData(contribution);
        const isService = category === "services";

        return (
            <motion.div
                key={formatted.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
            >
                <div className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                    <div className="p-4">
                        <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${isService ? 'bg-orange-100' : contributionType === 'donations' ? 'bg-green-100' : 'bg-blue-100'} flex items-center justify-center`}>
                                <span className="text-xl">
                                    {getContributionTypeIcon()}
                                </span>
                            </div>

                            {/* Content */}
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <h3 className="font-bold text-lg text-gray-800">
                                        {formatted.offer}
                                    </h3>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${formatted.status === 'active' || formatted.status === 'Active'
                                        ? 'bg-green-100 text-green-700 border border-green-200'
                                        : formatted.status === 'donated' || formatted.status === 'Donated'
                                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                                        }`}>
                                        {formatted.status === 'active' || formatted.status === 'Active'
                                            ? 'نشط'
                                            : formatted.status === 'donated' || formatted.status === 'Donated'
                                                ? 'تم التبرع'
                                                : 'غير نشط'}
                                    </span>
                                </div>

                                {/* Details */}
                                <div className="mt-2 space-y-2">
                                    <div className="flex flex-wrap gap-2">
                                        {!isService && formatted.category?.name && (
                                            <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded border">
                                                {formatted.category.name}
                                            </span>
                                        )}
                                        {isService && formatted.service?.name && (
                                            <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded border">
                                                {formatted.service.name}
                                            </span>
                                        )}
                                        {formatted._state?.name && (
                                            <span className="px-2 py-1 bg-gray-50 text-gray-600 text-xs rounded border">
                                                {formatted._state.name}
                                            </span>
                                        )}
                                    </div>

                                    <div className="text-sm text-gray-600">
                                        <div className="flex items-center gap-4">
                                            <span>الكمية: <strong>{formatted.quantity}</strong></span>
                                            {!isService && formatted._unit?.name && (
                                                <span>الوحدة: <strong>{formatted._unit.name}</strong></span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center pt-2 border-t">
                                        <div className="text-xs text-gray-400">
                                            {new Date(formatted.created_at).toLocaleDateString("ar-SA", {
                                                year: "numeric",
                                                month: "short",
                                                day: "numeric",
                                            })}
                                        </div>
                                        <Link
                                            to={`/home/contribution/${category}/${formatted.id}`}
                                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            عرض التفاصيل
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    };

    // Render a single order item - Compact version for combined view
    const renderOrderItemCompact = (order, index) => {
        const formatted = formatOrderData(order);

        return (
            <motion.div
                key={formatted.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
            >
                <Link
                    to={`/home/profile/in-kind-order/${formatted.id}`}
                    className="block p-3 bg-white border rounded-lg hover:shadow-md transition-all hover:border-blue-300"
                >
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${formatted.inKindStatus === 'Received' ? 'bg-green-500' :
                                    formatted.inKindStatus === 'Delivered' ? 'bg-orange-500' :
                                        formatted.inKindStatus === 'InTransit' ? 'bg-yellow-500' :
                                            formatted.inKindStatus === 'Dispatched' ? 'bg-blue-500' :
                                                'bg-gray-300'
                                }`}></div>
                            <div>
                                <div className="font-medium text-gray-800">طلب #{formatted.id}</div>
                                <div className="text-xs text-gray-500">
                                    {formatted.quantity} وحدة
                                </div>
                            </div>
                        </div>
                        <div className="text-left">
                            <div className={`px-2 py-1 rounded text-xs font-medium ${getOrderStatusColor(formatted.inKindStatus)}`}>
                                {translateOrderStatus(formatted.inKindStatus)}
                            </div>
                        </div>
                    </div>
                </Link>
            </motion.div>
        );
    };

    // Render orders section (for combined view)
    const renderOrdersSection = () => {
        const formattedOrders = Array.isArray(orders) ? orders.map(formatOrderData) : [];

        if (formattedOrders.length === 0) {
            return (
                <div className="text-center py-8 bg-gray-50 rounded-xl border">
                    <div className="text-gray-400 text-4xl mb-3">📦</div>
                    <div className="font-medium text-gray-600">لا توجد طلبات حالياً</div>
                    <div className="text-sm text-gray-500 mt-1">لم يتم إنشاء أي طلبات للمساهمات الحالية</div>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-lg text-gray-800">آخر الطلبات</h3>
                    <button
                        onClick={() => setViewMode("orders")}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                        عرض الكل ({formattedOrders.length})
                    </button>
                </div>
                {formattedOrders.slice(0, 5).map((order, index) => renderOrderItemCompact(order, index))}
                {formattedOrders.length > 5 && (
                    <div className="text-center pt-2">
                        <button
                            onClick={() => setViewMode("orders")}
                            className="text-sm text-gray-600 hover:text-gray-800"
                        >
                            عرض {formattedOrders.length - 5} طلب إضافي
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Render contributions section (for combined view)
    const renderContributionsSection = () => {
        const formattedContributions = Array.isArray(contributions) ? contributions.map(formatContributionData) : [];

        if (formattedContributions.length === 0) {
            return (
                <div className="text-center py-8 bg-gray-50 rounded-xl border">
                    <div className="text-gray-400 text-4xl mb-3">{getContributionTypeIcon()}</div>
                    <div className="font-medium text-gray-600">لا توجد مساهمات حالياً</div>
                    <div className="text-sm text-gray-500 mt-1">
                        {contributionType === "donations"
                            ? `ابدأ بتقديم ${category === "services" ? "خدمة" : "محصول"} عيني جديد`
                            : `اطلب ${category === "services" ? "خدمة" : "محصول"} عيني جديد`}
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-lg text-gray-800">المساهمات الحالية</h3>
                    <button
                        onClick={() => setViewMode("contributions")}
                        className="text-sm text-primary hover:text-primary-dark font-medium"
                    >
                        عرض الكل ({formattedContributions.length})
                    </button>
                </div>
                {formattedContributions.slice(0, 3).map((item, index) => renderContributionItem(item, index))}
                {formattedContributions.length > 3 && (
                    <div className="text-center pt-2">
                        <button
                            onClick={() => setViewMode("contributions")}
                            className="text-sm text-gray-600 hover:text-gray-800"
                        >
                            عرض {formattedContributions.length - 3} مساهمة إضافية
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Render combined view
    const renderCombinedView = () => {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Contributions Column */}
                <div>
                    <div className="bg-white rounded-xl p-5 border shadow-sm">
                        {renderContributionsSection()}
                    </div>
                </div>

                {/* Orders Column */}
                <div>
                    <div className="bg-white rounded-xl p-5 border shadow-sm">
                        {renderOrdersSection()}
                    </div>
                </div>
            </div>
        );
    };

    // Render full contributions view
    const renderFullContributionsView = () => {
        const formattedContributions = Array.isArray(contributions) ? contributions.map(formatContributionData) : [];

        if (isFetchingContributions) {
            return (
                <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="p-4 bg-gray-100 border rounded-xl shadow-sm animate-pulse">
                            <div className="h-5 bg-gray-300 rounded w-3/4 mb-3"></div>
                            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                        </div>
                    ))}
                </div>
            );
        }

        if (formattedContributions.length === 0) {
            return renderEmptyState("لا توجد مساهمات");
        }

        return (
            <div className="space-y-4">
                {formattedContributions.map((item, index) => renderContributionItem(item, index))}
            </div>
        );
    };

    // Render full orders view
    const renderFullOrdersView = () => {
        const formattedOrders = Array.isArray(orders) ? orders.map(formatOrderData) : [];

        if (isFetchingOrders) {
            return (
                <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="p-4 bg-gray-100 border rounded-xl shadow-sm animate-pulse">
                            <div className="h-5 bg-gray-300 rounded w-3/4 mb-3"></div>
                            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                        </div>
                    ))}
                </div>
            );
        }

        if (formattedOrders.length === 0) {
            return renderEmptyState("لا توجد طلبات مساهمة عينية");
        }

        return (
            <div className="space-y-4">
                {formattedOrders.map((order, index) => (
                    <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                        <div className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                            <div className="p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-bold text-lg text-gray-800">
                                            طلب #{order.id}
                                        </h3>
                                        <div className="text-sm text-gray-600 mt-1">
                                            {category === "crops" ? "محصول" : "خدمة"}
                                        </div>
                                    </div>
                                    <Link
                                        to={`/home/profile/in-kind-order/${order.id}`}
                                        className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm"
                                    >
                                        إدارة الطلب
                                    </Link>
                                </div>

                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getOrderStatusColor(order.inKindStatus)}`}>
                                            {translateOrderStatus(order.inKindStatus)}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {order.quantity} وحدة
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="space-y-2">
                                        <div>
                                            <div className="text-xs text-gray-500">تاريخ التسليم</div>
                                            <div className="font-medium">
                                                {order.deliveryDate
                                                    ? new Date(order.deliveryDate).toLocaleDateString('ar-SA')
                                                    : "غير محدد"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">تاريخ الإنشاء</div>
                                            <div className="font-medium">
                                                {new Date(order.createdAt).toLocaleDateString('ar-SA')}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div>
                                            <div className="text-xs text-gray-500">القيمة</div>
                                            <div className="font-medium">
                                                {order.price > 0 ? `${order.price} ريال` : "مجاناً"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">رقم الفاتورة</div>
                                            <div className="font-medium">
                                                {order.invoice?.id || "غير متوفر"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        );
    };

    // Render empty state
    const renderEmptyState = (message = "لا توجد مساهمات") => {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <motion.img
                    src={empty_box}
                    alt="Empty"
                    className="w-48 opacity-50"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                />
                <div className="font-bold text-xl text-gray-700 mt-6">
                    {message}
                </div>
                <div className="text-gray-500 mt-2 max-w-md">
                    {viewMode === "orders"
                        ? "لم يتم إنشاء أي طلبات مساهمة عينية حتى الآن"
                        : `لم تقم بأي ${contributionType === "donations" ? "تبرع" : "طلب"} في ${category === "services" ? "الخدمات" : "المحاصيل"} حتى الآن`}
                </div>
                {viewMode !== "both" && (
                    <button
                        onClick={() => setViewMode("both")}
                        className="mt-6 px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    >
                        العودة للعرض المشترك
                    </button>
                )}
            </div>
        );
    };

    if (!hasAccess()) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <motion.img
                    src={empty_box}
                    alt="No Access"
                    className="w-48 opacity-50"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                />
                <div className="font-bold text-xl text-gray-700 mt-6">
                    غير مصرح لك بالوصول
                </div>
                <div className="text-gray-500 mt-2 max-w-md">
                    {category === "services"
                        ? "يمكن فقط لمقدمي الخدمات التبرع بالخدمات، ويمكن فقط للجمعيات الاستهلاكية طلب الخدمات"
                        : "يمكن للجميع التبرع بالمحاصيل، ويمكن فقط للجمعيات الاستهلاكية طلب المحاصيل"}
                </div>
                <button
                    onClick={() => navigate("/home/in-kind")}
                    className="mt-6 px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
                >
                    العودة لصفحة المساهمات
                </button>
            </div>
        );
    }

    const availableTabs = getAvailableTabs();

    return (
        <div dir="rtl" className="w-full lg:px-20">
            {/* Header Section */}
            <div className={`${getContributionTypeColor()} p-6 rounded-2xl mb-6 border`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="text-3xl">{getContributionTypeIcon()}</div>
                            <div>
                                <h1 className="font-bold text-2xl lg:text-3xl text-gray-800">
                                    {contributionType === "donations"
                                        ? `عمليات مساهمة ثبات - ${category === "services" ? "الخدمات" : "المحاصيل"}`
                                        : `عمليات مساهمة ثبات - ${category === "services" ? "الخدمات" : "المحاصيل"}`}
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    إدارة المساهمات العينية وتتبع طلباتها
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* View Mode Toggle - Prominent Design */}
                    <div className="flex bg-white p-1 rounded-xl shadow-sm border">
                        <button
                            onClick={() => setViewMode("both")}
                            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${viewMode === "both"
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                }`}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-lg">📊</span>
                                <span>عرض مشترك</span>
                            </div>
                        </button>
                        <button
                            onClick={() => setViewMode("contributions")}
                            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${viewMode === "contributions"
                                    ? "bg-green-500 text-white shadow-sm"
                                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                }`}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-lg">{getContributionTypeIcon()}</span>
                                <span>المساهمات</span>
                            </div>
                        </button>
                        <button
                            onClick={() => setViewMode("orders")}
                            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${viewMode === "orders"
                                    ? "bg-blue-500 text-white shadow-sm"
                                    : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                }`}
                        >
                            <div className="flex items-center gap-2">
                                <span className="text-lg">📦</span>
                                <span>الطلبات</span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            {renderStatsCard()}

            {/* Navigation Tabs */}
            <div className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-2 h-8 bg-primary rounded-full"></div>
                    <h2 className="font-bold text-xl text-gray-800">
                        تصفية حسب النوع
                    </h2>
                </div>

                <div className="flex flex-wrap gap-3">
                    {availableTabs.map(tab => (
                        <Link
                            key={tab.id}
                            to={tab.path}
                            className={`px-5 py-3 rounded-xl font-medium transition-all ${tab.active
                                ? `${tab.color} bg-white border-2 shadow-sm`
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent"
                                }`}
                        >
                            {tab.label}
                        </Link>
                    ))}
                </div>
            </div>

            {/* Content Section */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className={`w-2 h-8 ${viewMode === "both" ? "bg-primary" :
                                viewMode === "contributions" ? "bg-green-500" :
                                    "bg-blue-500"
                            } rounded-full`}></div>
                        <h2 className="font-bold text-xl text-gray-800">
                            {viewMode === "both" ? "نظرة عامة" :
                                viewMode === "contributions" ? "قائمة المساهمات" :
                                    "طلبات المساهمة العينية"}
                        </h2>
                    </div>
                    <div className="text-sm text-gray-500">
                        {viewMode === "both" ? "عرض موجز" :
                            viewMode === "contributions" ? `${stats.contributions} مساهمة` :
                                `${stats.orders} طلب`}
                    </div>
                </div>

                {/* Content based on view mode */}
                {viewMode === "both" ? (
                    renderCombinedView()
                ) : viewMode === "contributions" ? (
                    renderFullContributionsView()
                ) : (
                    renderFullOrdersView()
                )}
            </div>

            {/* Quick Actions Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                    <button
                        onClick={() => navigate("/home/in-kind")}
                        className="flex-1 px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors text-center"
                    >
                        إنشاء مساهمة جديدة
                    </button>
                    {viewMode !== "both" && (
                        <button
                            onClick={() => setViewMode("both")}
                            className="flex-1 px-6 py-3 bg-white text-primary border-2 border-primary rounded-xl font-medium hover:bg-primary/5 transition-colors text-center"
                        >
                            العودة للعرض المشترك
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InKindContribution;